/**
 * Delete Confirmation Dialog Component
 * 
 * A professional-looking confirmation dialog for delete operations with:
 * - Warning styling with red accents
 * - Detailed information about what will be deleted
 * - Clear action buttons with loading states
 * - Backdrop blur and smooth animations
 */

import React from 'react'
import { AlertTriangle, X } from 'lucide-react'
import { Button } from './Button'
import { cn } from '../../utils/cn'

interface DeleteConfirmDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean
  /** Title of the item being deleted */
  title: string
  /** Type of item being deleted (e.g., "chatbot", "team", "agent") */
  itemType: string
  /** List of what will be deleted */
  deletionItems: string[]
  /** Whether the deletion is in progress */
  isDeleting?: boolean
  /** Callback when user confirms deletion */
  onConfirm: () => void
  /** Callback when user cancels deletion */
  onCancel: () => void
}

export const DeleteConfirmDialog: React.FC<DeleteConfirmDialogProps> = ({
  isOpen,
  title,
  itemType,
  deletionItems,
  isDeleting = false,
  onConfirm,
  onCancel
}) => {
  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 transition-opacity duration-300"
        onClick={onCancel}
      />
      
      {/* Dialog */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div 
          className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-auto transform transition-all duration-300 scale-100"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Delete {itemType}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  This action cannot be undone
                </p>
              </div>
            </div>
            
            <button
              onClick={onCancel}
              className="p-1 rounded-full hover:bg-gray-100 transition-colors"
              disabled={isDeleting}
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Item being deleted */}
            <div className="mb-6">
              <p className="text-gray-700 mb-2">
                You are about to delete:
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="font-medium text-red-900">"{title}"</p>
              </div>
            </div>

            {/* What will be deleted */}
            <div className="mb-6">
              <p className="text-gray-700 mb-3 font-medium">
                This will permanently delete:
              </p>
              <ul className="space-y-2">
                {deletionItems.map((item, index) => (
                  <li key={index} className="flex items-start space-x-2 text-sm text-gray-600">
                    <span className="text-red-500 mt-0.5">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Warning */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex items-start space-x-2">
                <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-yellow-800">
                    Warning: This action is irreversible
                  </p>
                  <p className="text-sm text-yellow-700 mt-1">
                    All data associated with this {itemType} will be permanently lost and cannot be recovered.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
            <Button
              variant="outline"
              onClick={onCancel}
              disabled={isDeleting}
              className="min-w-[100px]"
            >
              Cancel
            </Button>
            <Button
              onClick={onConfirm}
              disabled={isDeleting}
              className={cn(
                "min-w-[100px] bg-red-600 hover:bg-red-700 text-white",
                isDeleting && "opacity-50 cursor-not-allowed"
              )}
            >
              {isDeleting ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Deleting...</span>
                </div>
              ) : (
                'Delete Forever'
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}